import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Briefcase, Award, Mic } from "lucide-react";
import { http } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-2xl bg-[#F7F3EB] p-4"><div className="font-display text-xl text-[#2C4A3E]" data-testid={testId}>{value}</div><div className="text-[11px] uppercase tracking-widest text-stone-500 mt-1">{label}</div></div>
);

export const PathwayProgress = ({ classId, studentId }) => {
  const [d, setD] = useState(null);
  useEffect(() => { http.get(`/classes/${classId}/students/${studentId}/pathway`).then((r) => setD(r.data)).catch(() => {}); }, [classId, studentId]);
  if (!d?.eligible) return null;
  const b = d.business;
  const lp = d.pitches.latest;
  return (
    <section className="mt-10 rounded-3xl bg-white/60 border border-[#E6DEC3] p-6" data-testid="pathway-progress">
      <p className="text-xs uppercase tracking-widest text-stone-500 inline-flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" /> Career Pathway</p>
      <h3 className="font-display text-2xl text-[#2C4A3E] mt-1">GED readiness</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
        {d.ged.map((g) => (
          <div key={g.id} className="rounded-2xl bg-[#F7F3EB] p-4" data-testid={`pp-ged-${g.id}`}>
            <p className="font-medium text-[#2C4A3E] text-sm">{g.name}</p>
            <p className="text-xs text-stone-500 mt-1">{g.lessons_done}/{g.lessons_total} lessons · practice: {g.readiness.status}{g.readiness.pct != null && ` (${g.readiness.pct}%)`}</p>
            {g.latest_exam ? <p className="mt-2 text-sm"><b className="font-display text-xl text-[#2C4A3E]">{g.latest_exam.est_score}</b> <span className="text-xs text-stone-500">est. · {g.latest_exam.band} · {g.latest_exam.date}</span></p>
              : <p className="mt-2 text-xs text-stone-400">No full-length exam yet</p>}
            {g.latest_exam?.study_next?.length > 0 && <p className="text-[11px] text-stone-500 mt-1">Study next: {g.latest_exam.study_next.join("; ")}</p>}
          </div>
        ))}
      </div>
      <h3 className="font-display text-2xl text-[#2C4A3E] mt-8">Business Launch</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <Stat label="Modules" value={`${b.modules_done}/${b.modules_total}`} testId="pp-modules" />
        <Stat label="Plan sections" value={`${b.plan_filled}/${b.plan_total}`} testId="pp-plan" />
        <Stat label="Pitches" value={d.pitches.count} testId="pp-pitches" />
        <Stat label="Certificate" value={d.certificate ? <a href={`/certificate/${d.certificate.id}`} className="inline-flex items-center gap-1 text-[#D99B26]" data-testid="pp-certificate-link"><Award className="w-4 h-4" />{d.certificate.id}</a> : "Not yet"} testId="pp-certificate" />
      </div>
      {lp && <p className="text-sm text-stone-600 mt-3 inline-flex items-center gap-2" data-testid="pp-latest-pitch"><Mic className="w-4 h-4" /> Latest pitch: clarity {lp.scores.clarity}/5, confidence {lp.scores.confidence}/5, structure {lp.scores.structure}/5, ask {lp.scores.call_to_action}/5 · {lp.metrics.wpm ?? "-"} wpm</p>}
      {b.plan_filled > 0 && (
        <Accordion type="single" collapsible className="mt-4" data-testid="pp-plan-accordion">
          <AccordionItem value="plan" className="border-[#E6DEC3]">
            <AccordionTrigger className="text-sm">Read the business plan</AccordionTrigger>
            <AccordionContent>{b.plan.filter((s) => s.text).map((s) => <div key={s.title} className="mt-3"><p className="font-semibold text-[#2C4A3E]">{s.title}</p><div className="lesson-md text-sm"><ReactMarkdown>{s.text}</ReactMarkdown></div></div>)}</AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </section>
  );
};
